import { defineComponent as _defineComponent } from 'vue';
import { createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeStyle as _normalizeStyle, toDisplayString as _toDisplayString, createStaticVNode as _createStaticVNode } from "vue";
const _hoisted_1 = { class: "paypal-checkout-container" };
const _hoisted_2 = {
    key: 0,
    class: "fixed inset-0 bg-black/50 backdrop-blur-sm z-[200] flex items-center justify-center",
    style: { "pointer-events": "all" }
};
const _hoisted_3 = { class: "paypal-button-wrapper flex justify-center items-center w-full" };
const _hoisted_4 = {
    key: 0,
    class: "paypal-loading-overlay"
};
const _hoisted_5 = {
    key: 1,
    class: "payment-status mt-4"
};
const _hoisted_6 = {
    key: 0,
    class: "status-processing"
};
const _hoisted_7 = {
    key: 1,
    class: "status-success"
};
const _hoisted_8 = {
    key: 2,
    class: "error-state mt-4"
};
const _hoisted_9 = { class: "flex" };
const _hoisted_10 = { class: "flex-shrink-0" };
const _hoisted_11 = { class: "ml-3" };
const _hoisted_12 = { class: "mt-3" };
import { ref, onMounted, inject, watch, computed } from 'vue';
import { loadScript } from '@paypal/paypal-js';
import { useCheckoutStore } from '@shared'; // ✅ DISCOUNT: Get current discount code from store
import { useTheme } from '@shared/composables/useTheme';
export default /*@__PURE__*/ _defineComponent({
    __name: 'PayPalCheckout',
    props: {
        amount: {},
        currency: {},
        orderData: {},
        paymentConfig: { default: null },
        disabled: { type: Boolean },
        cartItems: {},
        deliveryType: {},
        deliveryAddress: {},
        customerInfo: {},
        specialInstructions: {},
        location: {},
        discountCode: {}
    },
    emits: ["payment-success", "payment-error", "payment-cancel"],
    setup(__props, { expose: __expose, emit: __emit }) {
        const props = __props;
        const emit = __emit;
        // ✅ DISCOUNT: Get current discount code from checkout store
        const checkoutStore = useCheckoutStore();
        // ✅ Inject API Client for proper URL handling
        const apiClient = inject('apiClient');
        if (!apiClient) {
            throw new Error('RestaurantApiClient not provided');
        }
        const paypalButtonContainer = ref();
        const isLoading = ref(true);
        const error = ref('');
        const paymentConfig = ref(null);
        const paymentStatus = ref('idle');
        const showTransitionOverlay = ref(false); // ✅ NEW: Temporary overlay until PayPal window opens
        const { theme } = useTheme();
        const paypalErrorColor = computed(() => theme.value.colors.danger || theme.value.colors.primary);
        const paypalErrorContainerStyle = computed(() => ({
            backgroundColor: `${paypalErrorColor.value}12`,
            borderColor: `${paypalErrorColor.value}55`
        }));
        const paypalRetryStyle = computed(() => ({
            backgroundColor: `${paypalErrorColor.value}22`,
            color: paypalErrorColor.value
        }));
        const retryPayment = () => {
            error.value = '';
            paymentStatus.value = 'idle';
            initializePayPalButtons();
        };
        onMounted(async () => {
            await loadPaymentConfiguration();
            await loadPayPalScript();
            initializePayPalButtons();
        });
        // ✅ Watch for disabled prop changes and re-render buttons
        watch(() => props.disabled, () => {
            console.log('🔄 [PayPalCheckout] Disabled state changed:', props.disabled);
            initializePayPalButtons();
        });
        // No cleanup needed - @paypal/paypal-js handles script lifecycle
        const loadPaymentConfiguration = async () => {
            // ✅ Use payment config from props (from backend) or fallback
            if (props.paymentConfig) {
                paymentConfig.value = props.paymentConfig;
                console.log('💳 [PayPalCheckout] Using payment config from props:', props.paymentConfig);
            }
            else {
                // Fallback if no config provided
                paymentConfig.value = {
                    enabled: true,
                    manual: {
                        enabled: true,
                        isDefault: true
                    },
                    paypal: {
                        enabled: true,
                        clientId: '', // Will cause error - forces proper config
                        fundingSources: { paypal: true, card: true }
                    }
                };
                console.warn('⚠️ [PayPalCheckout] No payment config provided in props, using fallback');
            }
        };
        const loadPayPalScript = async () => {
            // ✅ Get PayPal Client ID from props (from backend)
            let clientId = props.paymentConfig?.paypal?.clientId || paymentConfig.value?.paypal?.clientId;
            if (!clientId) {
                error.value = 'PayPal Client ID nicht konfiguriert';
                isLoading.value = false;
                throw new Error('PayPal Client ID missing');
            }
            // 🔍 DEBUG: Log vollständige Client ID für Diagnose
            console.log('🔍 [PayPalCheckout] Full Client ID:', clientId);
            console.log('🔍 [PayPalCheckout] Client ID length:', clientId.length);
            // Build funding sources parameters
            const fundingSources = props.paymentConfig?.paypal?.fundingSources || paymentConfig.value?.paypal.fundingSources || { paypal: true, card: true };
            const enabledFunding = Object.entries(fundingSources)
                .filter(([_, enabled]) => enabled)
                .map(([source, _]) => source);
            const allSources = ['paypal', 'card', 'sofort', 'giropay', 'ideal', 'sepa', 'bancontact', 'eps', 'p24', 'blik'];
            const disabledFunding = allSources.filter(source => !fundingSources[source]);
            console.log('💳 [PayPalCheckout] Loading PayPal SDK with @paypal/paypal-js:', {
                clientId: clientId.substring(0, 20) + '...',
                currency: props.currency,
                enabledFunding,
                disabledFunding
            });
            try {
                // ✅ Use official PayPal JS SDK loader
                const paypal = await loadScript({
                    clientId: clientId,
                    currency: props.currency,
                    locale: 'de_DE',
                    enableFunding: enabledFunding,
                    disableFunding: disabledFunding
                });
                if (!paypal) {
                    throw new Error('PayPal SDK returned null');
                }
                console.log('✅ [PayPalCheckout] PayPal SDK loaded successfully');
                isLoading.value = false;
            }
            catch (err) {
                console.error('❌ [PayPalCheckout] Failed to load PayPal SDK:', err);
                // ✅ Provide helpful error message based on error type
                const errorMessage = err.message || String(err);
                if (errorMessage.includes('failed to load')) {
                    error.value = 'PayPal SDK konnte nicht geladen werden. Bitte deaktivieren Sie Ad-Blocker und versuchen Sie es erneut.';
                }
                else if (errorMessage.includes('network')) {
                    error.value = 'Netzwerkfehler beim Laden von PayPal. Bitte prüfen Sie Ihre Internetverbindung.';
                }
                else {
                    error.value = 'PayPal ist momentan nicht verfügbar. Bitte wählen Sie eine andere Zahlungsmethode.';
                }
                isLoading.value = false;
                // ✅ Don't throw - allow page to continue with error message displayed
                console.warn('⚠️ [PayPalCheckout] Continuing without PayPal - error handled gracefully');
            }
        };
        const initializePayPalButtons = () => {
            if (!paypalButtonContainer.value || !window.paypal) {
                return;
            }
            // Clear existing PayPal buttons to prevent duplicates
            if (paypalButtonContainer.value) {
                paypalButtonContainer.value.innerHTML = '';
            }
            // ✅ Show disabled state if validation fails (e.g., minimum order not reached)
            if (props.disabled) {
                paypalButtonContainer.value.innerHTML = `
      <div class="paypal-disabled-state p-4 bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg text-center mx-auto">
        <div class="flex items-center justify-center text-gray-400 mb-2">
          <i class="fas fa-lock text-2xl"></i>
        </div>
        <p class="text-sm font-medium text-gray-600">PayPal-Zahlung nicht verfügbar</p>
        <p class="text-xs text-gray-500 mt-1">Bitte erfüllen Sie alle Bestellanforderungen</p>
      </div>
    `;
                isLoading.value = false;
                return;
            }
            try {
                window.paypal.Buttons({
                    // 🎨 Enhanced Button Styling
                    style: {
                        layout: 'vertical',
                        color: 'blue',
                        shape: 'rect',
                        label: 'checkout',
                        height: 50,
                        tagline: false
                    },
                    // 💰 Funding Sources (project-specific)
                    fundingSource: null, // Allows all enabled funding sources
                    // ✅ NEW: Show overlay when button clicked, hide after 3 seconds
                    onClick: () => {
                        console.log('🖱️ PayPal button clicked - showing transition overlay');
                        showTransitionOverlay.value = true;
                        // Auto-hide after 3 seconds (PayPal's own overlay should be visible by then)
                        setTimeout(() => {
                            showTransitionOverlay.value = false;
                            console.log('⏱️ Transition overlay hidden');
                        }, 3000);
                    },
                    createOrder: async (_data, _actions) => {
                        try {
                            console.log('🚀 Creating PayPal order with cart data...');
                            // ✅ Set processing state to lock UI
                            paymentStatus.value = 'processing';
                            isLoading.value = true;
                            // ✅ Use new cart-based method if cart items provided
                            let orderData;
                            if (props.cartItems && props.cartItems.length > 0) {
                                // ✅ Transform cart items to match /orders endpoint structure
                                const transformedItems = props.cartItems.map((item) => ({
                                    productId: item.id || item.menuItem?.id || item.menuItem?._id,
                                    quantity: item.quantity || 1,
                                    extraIngredientIds: (item.extras || []).map((e) => e.id || e._id),
                                    removedIngredientIds: item.removedIngredients || [],
                                    selectedSizeId: item.selectedSize?.id,
                                    selectedMandatoryIngredients: item.selectedMandatoryIngredients || [], // ✅ ADD: Mandatory ingredients
                                    specialInstructions: item.specialInstructions || ''
                                }));
                                // ✅ DISCOUNT: Get discount code from store's discount info (most reliable source)
                                const discountFromInfo = checkoutStore.discountInfo?.code;
                                const discountCodeToSend = discountFromInfo || checkoutStore.discountCode || props.discountCode;
                                // ✅ DEBUG: Log discount code
                                console.log('🎟️ [PayPalCheckout] Discount Code:', {
                                    fromDiscountInfo: discountFromInfo,
                                    fromStore: checkoutStore.discountCode,
                                    fromProps: props.discountCode,
                                    willSend: discountCodeToSend ? [discountCodeToSend] : undefined
                                });
                                // ✅ Build order data matching manual payment structure
                                orderData = await apiClient.createPayPalOrderWithCart({
                                    customer: {
                                        name: props.customerInfo?.name || '',
                                        email: props.customerInfo?.email || '',
                                        phone: props.customerInfo?.phone || '',
                                        address: props.deliveryAddress || {}
                                    },
                                    items: transformedItems,
                                    orderType: props.deliveryType || 'pickup',
                                    deliveryInfo: props.deliveryType === 'delivery' ? {
                                        deliveryAddress: props.deliveryAddress
                                    } : {},
                                    payment: {
                                        method: 'paypal'
                                    },
                                    discountCodes: discountCodeToSend ? [discountCodeToSend] : undefined, // ✅ DISCOUNT: Use current discount code from store
                                    subtotalCent: undefined, // Backend will calculate
                                    totalCent: undefined // Backend will calculate
                                });
                                console.log('✅ PayPal order created with cart data:', orderData.data.orderID);
                            }
                            else {
                                // ❌ FALLBACK: Legacy method (deprecated - should not be used)
                                console.warn('⚠️ Using legacy PayPal method - cartItems not provided');
                                orderData = await apiClient.createPayPalOrder(props.amount, props.currency, {
                                    orderId: props.orderData?.orderId,
                                    customerEmail: props.orderData?.customerEmail,
                                    orderType: props.orderData?.orderType
                                });
                                console.log('✅ PayPal order created (legacy):', orderData.data.orderID);
                            }
                            // ✅ Reset loading state after order creation (PayPal popup will open now)
                            isLoading.value = false;
                            paymentStatus.value = 'idle';
                            return orderData.data.orderID;
                        }
                        catch (err) {
                            console.error('❌ PayPal order creation failed:', err);
                            error.value = `Fehler beim Erstellen der PayPal-Bestellung: ${err.message}`;
                            paymentStatus.value = 'error';
                            isLoading.value = false;
                            throw err;
                        }
                    },
                    onApprove: async (data, _actions) => {
                        try {
                            console.log('🔄 [PayPalCheckout] Capturing PayPal payment...', data.orderID);
                            // ✅ Use RestaurantApiClient for proper URL handling
                            const captureData = await apiClient.capturePayPalOrder(data.orderID);
                            console.log('✅ [PayPalCheckout] PayPal payment captured:', {
                                orderID: data.orderID,
                                captureId: captureData.data.captureId,
                                amount: captureData.data.amount,
                                orderNumber: captureData.data.orderNumber
                            });
                            console.log('🔍 [PayPalCheckout] Full capture response:', JSON.stringify(captureData, null, 2));
                            // ✅ FIX: Include orderNumber in success event (needed for navigation)
                            const successPayload = {
                                orderID: data.orderID,
                                captureId: captureData.data.captureId,
                                amount: captureData.data.amount,
                                orderNumber: captureData.data.orderNumber,
                                internalOrderId: captureData.data.internalOrderId,
                                orderStatus: captureData.data.orderStatus,
                                paymentStatus: captureData.data.paymentStatus
                            };
                            console.log('📤 [PayPalCheckout] Emitting payment-success event with payload:', successPayload);
                            emit('payment-success', successPayload);
                        }
                        catch (err) {
                            console.error('❌ PayPal payment capture failed:', err);
                            error.value = `Fehler beim Abschließen der PayPal-Zahlung: ${err.message}`;
                            emit('payment-error', err.message);
                        }
                    },
                    onError: (err) => {
                        error.value = 'PayPal-Fehler aufgetreten';
                        console.error('PayPal error:', err);
                        emit('payment-error', 'PayPal-Fehler aufgetreten');
                    },
                    onCancel: (data) => {
                        console.log('🚫 PayPal payment cancelled by user:', data);
                        console.log('🔍 Cancel data keys:', Object.keys(data || {}));
                        console.log('🔍 Cancel data orderID:', data?.orderID);
                        // ✅ FIX: Always emit cancel event (orderID may be undefined for some cancel types)
                        emit('payment-cancel', { orderID: data?.orderID });
                        // Reset states
                        paymentStatus.value = 'idle';
                        isLoading.value = false;
                        showTransitionOverlay.value = false;
                    }
                }).render(paypalButtonContainer.value);
            }
            catch (err) {
                error.value = `Fehler beim Initialisieren von PayPal: ${err.message}`;
            }
        };
        // Expose method to reinitialize if needed
        const reinitialize = () => {
            if (paypalButtonContainer.value) {
                paypalButtonContainer.value.innerHTML = '';
                initializePayPalButtons();
            }
        };
        __expose({
            reinitialize
        });
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _createCommentVNode(" ✅ NEW: Transition Overlay - Shows for 3s when PayPal button clicked "),
                (showTransitionOverlay.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_2, [...(_cache[0] || (_cache[0] = [
                            _createElementVNode("div", { class: "bg-white rounded-xl shadow-2xl p-8 max-w-md mx-4 text-center" }, [
                                _createElementVNode("div", { class: "animate-spin rounded-full h-16 w-16 border-4 border-blue-600 border-t-transparent mx-auto mb-4" }),
                                _createElementVNode("h3", { class: "text-xl font-bold text-gray-900 mb-2" }, "PayPal wird geöffnet..."),
                                _createElementVNode("p", { class: "text-gray-600" }, "Bitte warten Sie einen Moment")
                            ], -1 /* CACHED */)
                        ]))]))
                    : _createCommentVNode("v-if", true),
                _createCommentVNode(" PayPal Button Container "),
                _createElementVNode("div", _hoisted_3, [
                    _createElementVNode("div", {
                        ref_key: "paypalButtonContainer",
                        ref: paypalButtonContainer,
                        id: "paypal-button-container",
                        class: "min-h-[50px] rounded-lg w-full flex items-center"
                    }, null, 512 /* NEED_PATCH */),
                    _createCommentVNode(" Custom Loading Overlay "),
                    (isLoading.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_4, [...(_cache[1] || (_cache[1] = [
                                _createElementVNode("div", { class: "flex flex-col items-center justify-center h-full" }, [
                                    _createElementVNode("div", { class: "paypal-spinner" }),
                                    _createElementVNode("p", { class: "mt-3 text-sm font-medium text-gray-700" }, "PayPal wird geladen..."),
                                    _createElementVNode("p", { class: "text-xs text-gray-500" }, "Bitte warten Sie einen Moment")
                                ], -1 /* CACHED */)
                            ]))]))
                        : _createCommentVNode("v-if", true)
                ]),
                _createCommentVNode(" Payment Process Status "),
                (paymentStatus.value !== 'idle')
                    ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                        (paymentStatus.value === 'processing')
                            ? (_openBlock(), _createElementBlock("div", _hoisted_6, [...(_cache[2] || (_cache[2] = [
                                    _createElementVNode("div", { class: "flex items-center space-x-3 p-4 bg-blue-50 rounded-lg border border-blue-200" }, [
                                        _createElementVNode("div", { class: "animate-spin rounded-full h-5 w-5 border-2 border-blue-600 border-t-transparent" }),
                                        _createElementVNode("div", null, [
                                            _createElementVNode("div", { class: "font-medium text-blue-900" }, "Zahlung wird verarbeitet..."),
                                            _createElementVNode("div", { class: "text-sm text-blue-700" }, "Bitte schließen Sie dieses Fenster nicht")
                                        ])
                                    ], -1 /* CACHED */)
                                ]))]))
                            : _createCommentVNode("v-if", true),
                        (paymentStatus.value === 'success')
                            ? (_openBlock(), _createElementBlock("div", _hoisted_7, [...(_cache[3] || (_cache[3] = [
                                    _createStaticVNode("<div class=\"flex items-center space-x-3 p-4 bg-green-50 rounded-lg border border-green-200\" data-v-1a55be28><div class=\"flex-shrink-0\" data-v-1a55be28><svg class=\"h-5 w-5 text-green-400\" fill=\"currentColor\" viewBox=\"0 0 20 20\" data-v-1a55be28><path fill-rule=\"evenodd\" d=\"M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z\" clip-rule=\"evenodd\" data-v-1a55be28></path></svg></div><div data-v-1a55be28><div class=\"font-medium text-green-900\" data-v-1a55be28>Zahlung erfolgreich!</div><div class=\"text-sm text-green-700\" data-v-1a55be28>Ihre Bestellung wird bearbeitet</div></div></div>", 1)
                                ]))]))
                            : _createCommentVNode("v-if", true)
                    ]))
                    : _createCommentVNode("v-if", true),
                _createCommentVNode(" Error State "),
                (error.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                        _createElementVNode("div", {
                            class: "rounded-lg p-4 border",
                            style: _normalizeStyle(paypalErrorContainerStyle.value)
                        }, [
                            _createElementVNode("div", _hoisted_9, [
                                _createElementVNode("div", _hoisted_10, [
                                    (_openBlock(), _createElementBlock("svg", {
                                        class: "h-5 w-5",
                                        style: _normalizeStyle({ color: paypalErrorColor.value }),
                                        fill: "currentColor",
                                        viewBox: "0 0 20 20"
                                    }, [...(_cache[4] || (_cache[4] = [
                                            _createElementVNode("path", {
                                                "fill-rule": "evenodd",
                                                d: "M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z",
                                                "clip-rule": "evenodd"
                                            }, null, -1 /* CACHED */)
                                        ]))], 4 /* STYLE */))
                                ]),
                                _createElementVNode("div", _hoisted_11, [
                                    _createElementVNode("h3", {
                                        class: "text-sm font-medium",
                                        style: _normalizeStyle({ color: paypalErrorColor.value })
                                    }, "PayPal Fehler", 4 /* STYLE */),
                                    _createElementVNode("div", {
                                        class: "mt-1 text-sm",
                                        style: _normalizeStyle({ color: paypalErrorColor.value })
                                    }, [
                                        _createElementVNode("p", null, _toDisplayString(error.value), 1 /* TEXT */)
                                    ], 4 /* STYLE */),
                                    _createElementVNode("div", _hoisted_12, [
                                        _createElementVNode("button", {
                                            onClick: retryPayment,
                                            class: "px-3 py-1 rounded text-sm font-medium transition-colors",
                                            style: _normalizeStyle(paypalRetryStyle.value)
                                        }, " Erneut versuchen ", 4 /* STYLE */)
                                    ])
                                ])
                            ])
                        ], 4 /* STYLE */)
                    ]))
                    : _createCommentVNode("v-if", true)
            ]));
        };
    }
});
